import {useEffect, useRef} from "react";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@repo/data-gql";
import {DeployInfo, RequestInfo} from "@repo/shared-core";
import {useLocation} from "@repo/urls";

import type {
    ContentForPath,
    ContentForPath_contentRoute_listedPathData_course as ListedCourse,
    ContentForPath_contentRoute_listedPathData_content as ListedLearnableContent,
    ContentForPath_contentRoute_unlistedPathData_course as UnlistedCourse,
    ContentForPath_contentRoute_unlistedPathData_content as UnlistedLearnableContent,
    ContentForPath_contentRoute_unlistedPathData_lesson as UnlistedLesson,
} from "../__graphql-types__/ContentForPath.ts";
import type {Action} from "../data-access/content-cache/content-cache-types.ts";
import useContentCache from "../data-access/content-cache/use-content-cache.ts";
import {CONTENT_ROUTE_QUERY} from "../queries.ts";
import {
    mapGqlCourse,
    mapGqlLearnableContent,
    mapGqlLesson,
} from "../types/gql-type-mappers.ts";

import useContentExistsInEnglish from "./use-content-exists-in-english.ts";

export type GenericContentRouterResult<TData> =
    | {
          status: "error";
      }
    | {
          status: "loading";
      }
    | {
          status: "success";
          result: "not-found" | "not-translated" | "not-available-stt";
      }
    | {
          status: "success";
          result: "redirect";
          redirectTo: string;
      }
    | ({
          status: "success";
          result: "valid-path";
      } & TData);

type ContentRouterResult = GenericContentRouterResult<Record<any, any>>;

type ContentDispatch = (action: Action) => void;

type CacheDispatchData = {
    courseData: ListedCourse | null | undefined | UnlistedCourse;
    learnableContentData:
        | ListedLearnableContent
        | null
        | undefined
        | UnlistedLearnableContent;
    unlisted: boolean;
    publishedContentVersion: string;
    lessonData: UnlistedLesson | null | undefined;
};

/**
 * Prepares queried Content data for putting in to the cache.
 * Determines listedness of the content and extracts listed or unlisted content
 * and published content version.
 */
export const prepareContentQueryDataForCacheDispatch = (
    data?: ContentForPath | null,
): CacheDispatchData => {
    const listedCourseData = data?.contentRoute?.listedPathData?.course;
    const unlistedCourseData = data?.contentRoute?.unlistedPathData?.course;
    const listedLearnableContentData =
        data?.contentRoute?.listedPathData?.content;
    const unlistedLearnableContentData =
        data?.contentRoute?.unlistedPathData?.content;
    const listedLessonData = data?.contentRoute?.listedPathData?.lesson;
    const unlistedLessonData = data?.contentRoute?.unlistedPathData?.lesson;

    const unlisted =
        (!listedCourseData && !!unlistedCourseData) ||
        (!listedLearnableContentData && !!unlistedLearnableContentData);

    const courseData = unlisted ? unlistedCourseData : listedCourseData;
    const learnableContentData = unlisted
        ? unlistedLearnableContentData
        : listedLearnableContentData;
    const lessonData = unlisted ? unlistedLessonData : listedLessonData;

    // publishedContentVersion is always null in dev.
    const publishedContentVersion =
        data?.publishedContentVersion.contentVersion ?? "";

    // Curation Pages aren't supported by the content router yet. Don't send
    // back data since it can't be used.
    if (courseData?.contentKind === "CurationPage") {
        return {
            courseData: null,
            learnableContentData: null,
            unlisted,
            publishedContentVersion,
            lessonData: null,
        };
    }

    return {
        courseData,
        learnableContentData,
        publishedContentVersion,
        unlisted,
        // Only include lesson data if we're working around CL-326. INTL Unit
        // Tests and Quizzes in unlisted or missing lessons still should be
        // accessible. We load their lesson data separately. Restrict this here
        // since we don't need this data for other content and it limits the
        // hack fallout.
        lessonData:
            learnableContentData?.contentKind === "UnitTest" ||
            learnableContentData?.contentKind === "Quiz"
                ? lessonData
                : null,
    };
};

export const getUnlistedUnitIds = (
    courseData: ListedCourse | UnlistedCourse,
): Set<string> => {
    const units = courseData.unitChildren?.filter(isTruthy) ?? [];
    return new Set(
        units
            .filter((unit) => unit.unlistedAncestorIds?.includes(courseData.id))
            .map((unit) => unit.id),
    );
};

// Dispatch an update to the correct (listed/unlisted) cache if data is
// available. Returns true if the cache will be updated.
const maybeUpdateCache = (
    contentDispatch: ContentDispatch,
    data?: ContentForPath | null,
): boolean => {
    const {
        courseData,
        learnableContentData,
        unlisted,
        publishedContentVersion,
        lessonData,
    } = prepareContentQueryDataForCacheDispatch(data);

    if (courseData && learnableContentData) {
        contentDispatch({
            unlisted,
            publishedContentVersion,
            type: "SET_COURSE_AND_CONTENT",
            data: {
                course: mapGqlCourse(courseData),
                unlistedUnitIds: getUnlistedUnitIds(courseData),
                content: mapGqlLearnableContent(learnableContentData),
                lesson: lessonData ? mapGqlLesson(lessonData) : undefined,
            },
        });
        return true;
    }
    if (courseData) {
        contentDispatch({
            unlisted,
            publishedContentVersion,
            type: "SET_COURSE",
            data: {
                course: mapGqlCourse(courseData),
                unlistedUnitIds: getUnlistedUnitIds(courseData),
            },
        });
        return true;
    }

    return false;
};

const removeLeadingSlash = (path: string): string => path.replace(/^\//, "");

// fetches the content for the given path, along with its associated course,
// and validates the path.
const useContentRouter = (courseSlug?: string | null): ContentRouterResult => {
    const location = useLocation();
    const contentPath = removeLeadingSlash(location.pathname);

    const {
        dispatch: contentDispatch,
        state: {
            publishedContentVersion: cacheContentVersion,
            courseIdBySlug,
            courseById,
        },
    } = useContentCache();
    // The last contentPath that we cleared the unlisted cache for. Use this to
    // determine if the path has changed and we need to clear the cache for a
    // new path.
    const lastClearedContentPath = useRef<string | null | undefined>(null);

    // Any time the contentPath changes, clear the Unlisted cache.
    // The Unlisted cache can have content that is only available for the
    // specific path it was loaded for.
    useEffect(() => {
        // Verify the path has actually changed since the last time we did this.
        if (lastClearedContentPath.current !== contentPath) {
            contentDispatch({
                unlisted: true,
                type: "CLEAR_CONTENT_CACHE",
            });
        }
        lastClearedContentPath.current = contentPath;
    }, [contentDispatch, contentPath]);
    // NOTE(angelo): ^ contentDispatch isn't stable when publishedContentVersion
    // changes. Which happens when the cache is cleared.

    // Determine if we have cached data for this route already. If we do, we'll
    // not query the server and just render the cached data.
    const courseId = courseSlug ? courseIdBySlug[courseSlug] : null;
    const needsContentPathQuery = !courseId || !courseById[courseId];

    // We're not going to actually check *what* content is missing, just fetch
    // it all right now.
    const pathQueryResult = useFastlyQuery(CONTENT_ROUTE_QUERY, {
        skip: !needsContentPathQuery,
        variables: {
            path: contentPath,
            countryCode: RequestInfo.remoteCountry,
            kaLocale: RequestInfo.kaLocale,
            // Provide the current cache's content version, if available. If
            // not, fall back to the version embedded in the page (from the
            // initial request).
            clientPublishedContentVersion:
                cacheContentVersion || DeployInfo.commitSHA,
        },
    });

    useEffect(() => {
        if (needsContentPathQuery && pathQueryResult.status === "success") {
            maybeUpdateCache(contentDispatch, pathQueryResult.data);
        }
    }, [
        contentDispatch,
        needsContentPathQuery,
        // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ contentRoute: ContentForPath_contentRoute | null | undefined; publishedContentVersion: ContentForPath_publishedContentVersion; }>'.
        pathQueryResult.data,
        pathQueryResult.status,
    ]);

    const isNotFound =
        pathQueryResult.status === "success" &&
        !pathQueryResult.data.contentRoute?.resolvedPath;
    const skipEnglishCheck = !isNotFound || RequestInfo.kaLocale === "en";

    const existsInEnglishResult = useContentExistsInEnglish(contentPath, {
        skip: skipEnglishCheck,
    });

    if (needsContentPathQuery) {
        if (
            pathQueryResult.status === "error" ||
            pathQueryResult.status === "aborted"
        ) {
            return {status: "error"};
        }
        if (
            pathQueryResult.status === "loading" ||
            pathQueryResult.status === "no-data"
        ) {
            return {status: "loading"};
        }
    }

    // This will only be true when the course query has been fetched for our
    // exact current path. That's why we can do the redirect check. As the
    // user navigates within the course, the status will be "loading" instead
    // since the query is skipped (the course data is already cached). If the user navigates
    // to a redirected path again we'll still have the query cached, this will
    // run again, and redirect instantly.
    if (pathQueryResult.status === "success") {
        const {
            data: {contentRoute},
        } = pathQueryResult;

        if (!contentRoute?.resolvedPath) {
            if (skipEnglishCheck) {
                return {
                    status: "success",
                    result: "not-found",
                };
            }
            if (existsInEnglishResult.status === "success") {
                return {
                    status: "success",
                    result: existsInEnglishResult.existsInEnglish
                        ? "not-translated"
                        : "not-found",
                };
            }
            return existsInEnglishResult;
        }

        if (contentRoute.resolvedPath !== contentPath) {
            return {
                status: "success",
                result: "redirect",
                redirectTo: "/" + contentRoute.resolvedPath,
            };
        }

        // CurationPages can't be handled with the content router yet. Force
        // falling back to legacy data loading.
        if (
            contentRoute.listedPathData?.course?.contentKind ===
                "CurationPage" ||
            contentRoute.unlistedPathData?.course?.contentKind ===
                "CurationPage"
        ) {
            return {
                status: "success",
                result: "not-available-stt",
            };
        }
    }

    return {
        status: "success",
        result: "valid-path",
    };
};

export default useContentRouter;

export {
    maybeUpdateCache, // For testing
};
